import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Crown, CheckCircle2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MbtiBadge } from "@/lib/mbti";

export default function CaptainAttendance() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [players, setPlayers] = useState([]);
  const [entriesByPid, setEntriesByPid] = useState({});
  const [settings, setSettings] = useState({ current_week: 1, weeks_total: 10 });
  const [week, setWeek] = useState(1);
  const [busy, setBusy] = useState({});

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setWeek(r.data.current_week_computed || r.data.current_week || 1);
    });
  }, []);

  useEffect(() => {
    if (!week) return;
    api.get(`/captain/me?week=${week}`).then((r) => setStatus(r.data));
  }, [week]);

  useEffect(() => {
    if (!status?.is_captain || !status.club) return;
    api.get(`/players?club=${encodeURIComponent(status.club)}`).then((r) => {
      // Filter only own club's active players
      setPlayers(r.data.filter((p) => p.club === status.club && p.is_active !== false));
    });
    api.get(`/entries?week=${week}`).then((r) => {
      const map = {};
      r.data.forEach((e) => { map[e.player_id] = e; });
      setEntriesByPid(map);
    });
  }, [status, week]);

  const save = async (player_id, partial) => {
    const current = entriesByPid[player_id] || {};
    const payload = {
      player_id,
      week_number: week,
      presente: partial.presente ?? current.presente ?? true,
      pontual: partial.pontual ?? current.pontual ?? true,
      tpc: partial.tpc ?? current.tpc ?? true,
    };
    setBusy((b) => ({ ...b, [player_id]: true }));
    try {
      const { data } = await api.post("/entries/attendance", payload);
      setEntriesByPid((m) => ({ ...m, [player_id]: data }));
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy((b) => ({ ...b, [player_id]: false }));
  };

  if (status === null) {
    return <div className="kw-card p-8 text-center text-[#737373]">A verificar nomeação…</div>;
  }

  if (!status.is_captain) {
    return (
      <div className="kw-card p-8 text-center" data-testid="captain-not-assigned">
        <AlertTriangle className="w-10 h-10 text-[#FFB020] mx-auto mb-3" />
        <div className="kw-overline mb-2">Sem nomeação</div>
        <h1 className="font-display text-3xl font-black uppercase mb-2">Não és capitão esta semana</h1>
        <p className="text-[#A3A3A3] max-w-md mx-auto">
          O coach nomeia 1 capitão semanal por clube. Quando fores nomeado, esta página fica ativa para registares presença, pontualidade e TPC dos colegas.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 kw-fade-up" data-testid="captain-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Crown className="w-5 h-5 text-[#E50000]" />
            <div className="kw-overline">Capitão · Semana {week} · {status.club}</div>
          </div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            Registar <span className="text-[#E50000]">Reunião</span>
          </h1>
          <p className="text-[#A3A3A3] mt-2 text-sm max-w-2xl">
            Como capitão da semana, regista a presença, pontualidade e TPC dos colegas do clube <strong>{status.club}</strong>.
            O sistema marca automaticamente o teu próprio registo como <strong>&quot;Auto-registado&quot;</strong> para auditoria.
          </p>
        </div>
        <div className="kw-card px-5 py-3">
          <label className="kw-overline block mb-1">Semana</label>
          <input type="number" min="1" max={settings.weeks_total} className="kw-input !w-24"
            value={week} onChange={(e) => setWeek(Number(e.target.value))} data-testid="captain-week" />
        </div>
      </div>

      {players.length === 0 ? (
        <div className="kw-card p-8 text-center text-[#737373]">Não há consultores ativos no clube {status.club}.</div>
      ) : (
        <>
          {/* Desktop / tablet ≥md — table */}
          <div className="kw-card overflow-x-auto hidden md:block">
            <table className="w-full text-left text-sm min-w-[680px]" data-testid="captain-table">
              <thead className="kw-thead-sticky border-b border-[#222]">
                <tr className="kw-overline">
                  <th className="p-3 kw-th-sticky-col">Consultor</th>
                  <th className="p-3">Perfil</th>
                  <th className="p-3">Presente?</th>
                  <th className="p-3">Pontual?</th>
                  <th className="p-3">TPC</th>
                  <th className="p-3 text-right">Estado</th>
                </tr>
              </thead>
              <tbody>
                {players.map((p) => {
                  const e = entriesByPid[p.id];
                  const has = !!e;
                  const isSelf = p.id === status.player_id;
                  return (
                    <tr key={p.id} className="border-b border-[#1A1A1A] hover:bg-[#141414] bg-[#1A1A1A]">
                      <td className="p-3 font-semibold kw-col-sticky">
                        {p.name}
                        {isSelf && <span className="ml-2 text-[10px] uppercase tracking-widest font-bold text-[#FFB020]">Auto-registado</span>}
                      </td>
                      <td className="p-3"><MbtiBadge mbti={p.mbti} label={p.personality_label} compact /></td>
                      <td className="p-3"><BoolToggle value={has ? e.presente : true} onChange={(v) => save(p.id, { presente: v })} testid={`cap-presente-${p.id}`} /></td>
                      <td className="p-3"><BoolToggle value={has ? e.pontual : true} onChange={(v) => save(p.id, { pontual: v })} testid={`cap-pontual-${p.id}`} /></td>
                      <td className="p-3"><BoolToggle value={has ? e.tpc : true} onChange={(v) => save(p.id, { tpc: v })} testid={`cap-tpc-${p.id}`} /></td>
                      <td className="p-3 text-right text-xs">
                        {busy[p.id] ? <span className="text-[#A3A3A3]">A guardar…</span> :
                          has ? <span className="text-[#00FF88] inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Guardado</span> :
                          <span className="text-[#737373]">Por registar</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile <md — card layout (fixes sticky column breakage) */}
          <div className="md:hidden grid gap-3" data-testid="captain-cards">
            {players.map((p) => {
              const e = entriesByPid[p.id];
              const has = !!e;
              const isSelf = p.id === status.player_id;
              return (
                <div key={p.id} className="kw-card p-4 space-y-3" data-testid={`captain-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-base">{p.name}</div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <MbtiBadge mbti={p.mbti} label={p.personality_label} compact />
                        {isSelf && <span className="text-[9px] uppercase tracking-widest font-bold text-[#FFB020]">Auto-registado</span>}
                      </div>
                    </div>
                    <div className="text-[10px] text-right flex-shrink-0">
                      {busy[p.id] ? <span className="text-[#A3A3A3]">A guardar…</span> :
                        has ? <span className="text-[#00FF88] inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Guardado</span> :
                        <span className="text-[#737373]">Por registar</span>}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-[#222]">
                    <MobileField label="Presente" value={has ? e.presente : true} onChange={(v) => save(p.id, { presente: v })} testid={`cap-presente-${p.id}`} />
                    <MobileField label="Pontual" value={has ? e.pontual : true} onChange={(v) => save(p.id, { pontual: v })} testid={`cap-pontual-${p.id}`} />
                    <MobileField label="TPC" value={has ? e.tpc : true} onChange={(v) => save(p.id, { tpc: v })} testid={`cap-tpc-${p.id}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function MobileField({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-[#A3A3A3] font-bold mb-1 text-center">{label}</div>
      <div className="flex gap-1 justify-center" data-testid={testid}>
        <button type="button" onClick={() => onChange(true)}
          className={`flex-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest border ${value ? "bg-[#E50000] border-[#E50000] text-white" : "border-[#333] text-[#A3A3A3]"}`}>Sim</button>
        <button type="button" onClick={() => onChange(false)}
          className={`flex-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest border ${!value ? "bg-[#1A1A1A] border-white text-white" : "border-[#333] text-[#A3A3A3]"}`}>Não</button>
      </div>
    </div>
  );
}

function BoolToggle({ value, onChange, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      <button type="button" onClick={() => onChange(true)}
        className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest border ${value ? "bg-[#E50000] border-[#E50000] text-white" : "border-[#333] text-[#A3A3A3] hover:text-white"}`}>Sim</button>
      <button type="button" onClick={() => onChange(false)}
        className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest border ${!value ? "bg-[#1A1A1A] border-white text-white" : "border-[#333] text-[#A3A3A3] hover:text-white"}`}>Não</button>
    </div>
  );
}
